// Push Notifications Utility
// Handles service worker registration, push subscription, and notification permissions

// Check if push notifications are supported
export function isPushSupported() {
  return 'serviceWorker' in navigator && 
         'PushManager' in window && 
         'Notification' in window
}

// Check if running as PWA (installed to home screen)
export function isPWA() {
  return window.matchMedia('(display-mode: standalone)').matches ||
         window.navigator.standalone === true
}

// Get current notification permission
export function getNotificationPermission() {
  if (!('Notification' in window)) return 'unsupported'
  return Notification.permission // 'granted', 'denied', or 'default'
}

// Register service worker
export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) {
    console.warn('[Push] Service workers not supported')
    return null
  }
  
  try {
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/'
    })
    console.log('[Push] Service worker registered:', registration.scope)
    return registration
  } catch (error) {
    console.error('[Push] Service worker registration failed:', error)
    return null
  }
}

// Get existing service worker registration
export async function getServiceWorkerRegistration() {
  if (!('serviceWorker' in navigator)) return null
  return await navigator.serviceWorker.ready
}

// Request notification permission
export async function requestNotificationPermission() {
  if (!('Notification' in window)) {
    return { success: false, reason: 'unsupported' }
  }
  
  if (Notification.permission === 'granted') {
    return { success: true, permission: 'granted' }
  }
  
  if (Notification.permission === 'denied') {
    return { success: false, reason: 'denied' }
  }
  
  try {
    const permission = await Notification.requestPermission()
    return { 
      success: permission === 'granted', 
      permission 
    }
  } catch (error) {
    return { success: false, reason: error.message }
  }
}

// Subscribe to push notifications
export async function subscribeToPush(deviceId) {
  if (!isPushSupported()) {
    return { success: false, reason: 'Push notifications not supported' }
  }
  
  try {
    // Get service worker registration
    const registration = await getServiceWorkerRegistration()
    if (!registration) {
      // Try to register
      const newReg = await registerServiceWorker()
      if (!newReg) {
        return { success: false, reason: 'Service worker not available' }
      }
    }
    
    const swReg = await navigator.serviceWorker.ready
    
    // Check for existing subscription
    let subscription = await swReg.pushManager.getSubscription()
    
    if (!subscription) {
      // Get VAPID public key from server
      const keyResponse = await fetch('/api/push-vapid-key')
      if (!keyResponse.ok) {
        return { success: false, reason: 'Could not get VAPID key' }
      }
      const { publicKey } = await keyResponse.json()
      
      // Subscribe to push
      subscription = await swReg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey)
      })
      
      console.log('[Push] New subscription created')
    } else {
      console.log('[Push] Using existing subscription')
    }
    
    // Send subscription to server
    const response = await fetch('/api/push-subscribe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        deviceId,
        subscription: subscription.toJSON()
      })
    })
    
    if (!response.ok) {
      throw new Error('Failed to save subscription')
    }
    
    console.log('[Push] Subscription saved to server')
    return { success: true, subscription }
    
  } catch (error) {
    console.error('[Push] Subscription failed:', error)
    return { success: false, reason: error.message }
  }
}

// Unsubscribe from push notifications
export async function unsubscribeFromPush(deviceId) {
  try {
    const registration = await getServiceWorkerRegistration()
    if (!registration) return { success: true }
    
    const subscription = await registration.pushManager.getSubscription()
    if (subscription) {
      await subscription.unsubscribe()
      
      // Remove from server
      await fetch('/api/push-subscribe', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deviceId })
      })
      
      console.log('[Push] Unsubscribed')
    }
    
    return { success: true }
  } catch (error) {
    console.error('[Push] Unsubscribe failed:', error)
    return { success: false, reason: error.message }
  }
}

// Check if currently subscribed
export async function isSubscribedToPush() {
  try {
    const registration = await getServiceWorkerRegistration()
    if (!registration) return false
    
    const subscription = await registration.pushManager.getSubscription()
    return !!subscription
  } catch (error) {
    return false
  }
}

// Helper: Convert base64 to Uint8Array for VAPID key
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4)
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/')
  
  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

// Show local notification (fallback when SW not available)
export function showLocalNotification(title, body) {
  if (!('Notification' in window)) return
  if (Notification.permission !== 'granted') return
  
  const notification = new Notification(title, {
    body: body.substring(0, 100) + (body.length > 100 ? '...' : ''),
    icon: '/favicon.svg',
    tag: 'ai-complete'
  })
  
  notification.onclick = () => {
    window.focus()
    notification.close()
  }
  
  setTimeout(() => notification.close(), 5000)
}
